import { useState } from "react";

import PermissionsSelection from "common/components/PermissionsSelection";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { USER_PERMISSIONS_CATEGORIES } from "common/constants";
import { typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Member, UserPermissions } from "common/types";

import useMembersMutations from "../datahooks/useMembersMutations";

interface ChangeRoleDialogProps {
  member: Member;
  onClose: () => void;
  onBack: () => void;
}

export default function UpdatePermissions({
  member,
  onClose,
  onBack,
}: ChangeRoleDialogProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { email, role, permissions: currentPermissions } = member;

  const [permissions, setPermissions] =
    useState<UserPermissions>(currentPermissions);

  const { changeMemberRole, isChangingRole } = useMembersMutations();

  const isClient = role === "agency_client";

  const handleSave = () => {
    let updatedRole: Member["role"] = "agency_client";

    if (!isClient) {
      const isAdmin = typedObjectKeys(USER_PERMISSIONS_CATEGORIES).every(
        (key) => permissions.includes(`${key}_full`),
      );
      updatedRole = isAdmin ? "admin" : "member";
    }

    changeMemberRole({
      memberId: member.customer_id,
      permissions,
      role: updatedRole,
    }).then(onClose);
  };
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header
        title={<>Update permissions for {email}</>}
        subtitle={
          <>
            Give this {role === "agency_client" ? "client" : "member"} full
            access to the workspace, or limit which pages they can manage
          </>
        }
      />

      <Body>
        <PermissionsSelection
          permissions={permissions}
          setPermissions={setPermissions}
          isClient={isClient}
        />
      </Body>

      <Footer className="justify-end">
        <Button
          disabled={isChangingRole}
          variant="secondary-black"
          onClick={onBack}
        >
          Cancel
        </Button>

        <Button isLoading={isChangingRole} onClick={handleSave}>
          Save
        </Button>
      </Footer>
    </>
  );
}
